/** Cliente da API. Tudo autenticado por cookie de sessão (`credentials: include`). */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly issues?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    ...init,
    headers: {
      ...(init.body && typeof init.body === "string" ? { "content-type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (!res.ok) {
    let mensagem = `Erro ${res.status}`;
    let issues: unknown;
    try {
      const body = (await res.json()) as { error?: string; issues?: unknown };
      if (body.error) mensagem = body.error;
      issues = body.issues;
    } catch {
      /* resposta sem JSON */
    }
    throw new ApiError(res.status, mensagem, issues);
  }

  return (await res.json()) as T;
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", ...(body === undefined ? {} : { body: JSON.stringify(body) }) }),
  put: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  del: <T,>(path: string) => request<T>(path, { method: "DELETE" }),

  /** Foto vai como corpo cru — ver a nota em routes/meals.ts. */
  postImage: <T,>(path: string, blob: Blob, hint?: string) =>
    request<T>(`${path}${hint ? `?hint=${encodeURIComponent(hint)}` : ""}`, {
      method: "POST",
      body: blob,
      headers: { "content-type": blob.type || "image/jpeg" },
    }),
};

/* ─────────────────────────── Tipos da API ─────────────────────────── */

export interface TdeeEstimate {
  tdee: number | null;
  confidence: "none" | "low" | "medium" | "high";
  trendKgPerWeek: number | null;
  smoothedWeightKg: number | null;
  coverage: number;
  note: string | null;
}

export interface Readiness {
  score: number | null;
  band: "baixo" | "moderado" | "bom" | "otimo" | null;
  hrvAvailable: boolean;
  subjectiveAvailable: boolean;
  headline: string;
  note: string | null;
  components: { key: string; score: number; weight: number }[];
}

export interface MacroProgressRow {
  key: "kcal" | "proteinG" | "carbG" | "fatG";
  consumed: number;
  target: number;
  remaining: number;
  percent: number;
}

export interface Dashboard {
  today: string;
  user: { name: string; platform: string };
  cycle: { name: string; focus: string; goalDirection: string } | null;
  tdee: TdeeEstimate;
  target: { kcal: number; deltaKcal: number; expectedKgPerWeek: number; note: string | null } | null;
  macros: { kcal: number; proteinG: number; fatG: number; carbG: number; proteinPerKg: number } | null;
  progress: MacroProgressRow[] | null;
  consumed: { kcal: number; proteinG: number; carbG: number; fatG: number };
  intakeConfidence: number | null;
  weight: { todayKg: number | null; smoothedKg: number | null; trendKgPerWeek: number | null };
  readiness: Readiness;
  checkin: { feitoHoje: boolean; energy: number | null; soreness: number | null };
  mascotState: string;
  series: {
    date: string;
    kcal: number | null;
    proteinG: number | null;
    weightKg: number | null;
    mealCount: number;
  }[];
}

export interface SugestaoFoto {
  detectedName: string;
  grams: number;
  confidence: number;
  preparation: string | null;
  match: {
    foodId: string;
    name: string;
    source: string;
    verified: boolean;
    nutrients: { kcal: number; proteinG: number; carbG: number; fatG: number };
  } | null;
  alternatives: { foodId: string; name: string }[];
}

export interface RespostaFoto {
  photoKey: string;
  suggestions: SugestaoFoto[];
  questions: string[];
  note?: string | null;
  provider?: string;
  trainsOnInput?: boolean;
  error?: string;
}

export interface AlimentoBusca {
  id: string;
  name: string;
  brand: string | null;
  source: "taco" | "off" | "bootstrap" | "user";
  verified: boolean;
  per100g: { kcal: number; proteinG: number; carbG: number; fatG: number; fiberG?: number };
  defaultPortion: { label: string; grams: number } | null;
}
